// Super Admin "Edit Member" — correct any member detail, most importantly the
// login email (a wrong email blocks both password and Google sign-in).
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { DISTRICTS, LOCAL_BODY_TYPES } from "@/data/static";
import { GRAMA_PANCHAYATS_BY_DISTRICT_BLOCK, BLOCK_PANCHAYATS_BY_DISTRICT, MUNICIPALITIES_BY_DISTRICT, CORPORATIONS_BY_DISTRICT, DISTRICT_PANCHAYATS_BY_DISTRICT } from "@/data/localBodies";
import DatePicker from "@/components/registers/DatePicker";

// FastAPI 422 validation errors return detail as an array of {msg, loc, ...} —
// rendering that array crashes React (blank page). Always flatten to a string.
const errorText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
};

const FIELDS = ["full_name", "email", "mobile", "gender", "dob", "address", "employee_id", "designation",
  "department", "district", "local_body_type", "local_body_name", "block_panchayat",
  "date_of_joining", "years_of_service", "educational_qualification", "blood_group", "employment_type"];

export default function EditMemberDialog({ member, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (member) {
      const f = {};
      FIELDS.forEach(k => { f[k] = member[k] === null || member[k] === undefined ? "" : String(member[k]); });
      setForm(f);
    }
  }, [member]);

  if (!member || !form) return null;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const district = form.district;
  const lbType = form.local_body_type;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/members/${member.id}`, form);
      toast.success(`Member updated: ${data.full_name}`);
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(errorText(err, "Could not update member"));
    } finally {
      setSaving(false);
    }
  };

  const lbNameSelect = (placeholder, options, testid) => (
    <Select value={form.local_body_name} onValueChange={v => set("local_body_name", v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {(options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <Dialog open={!!member} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="edit-member-dialog">
        <DialogHeader>
          <DialogTitle>Edit Member — {member.full_name}</DialogTitle>
          <DialogDescription>
            Super Admin only. Changing the <strong>email changes the member&apos;s login</strong> — both password sign-in and Google sign-in use it. Membership ID, payment and status are never affected.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Full Name *</Label>
              <Input required value={form.full_name} onChange={e => set("full_name", e.target.value)} data-testid="em-full-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Email (login) *</Label>
              <Input required type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="em-email" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Mobile</Label>
              <Input value={form.mobile} onChange={e => set("mobile", e.target.value)} data-testid="em-mobile" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Gender</Label>
              <Select value={form.gender} onValueChange={v => set("gender", v)}>
                <SelectTrigger data-testid="em-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {["Male", "Female", "Other"].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Date of Birth</Label>
              <DatePicker value={form.dob} onChange={v => set("dob", v)} testid="em-dob" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Blood Group</Label>
              <Input value={form.blood_group} onChange={e => set("blood_group", e.target.value)} data-testid="em-blood-group" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold">Address</Label>
              <Textarea rows={2} value={form.address} onChange={e => set("address", e.target.value)} data-testid="em-address" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Employee ID</Label>
              <Input value={form.employee_id} onChange={e => set("employee_id", e.target.value)} data-testid="em-employee-id" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Designation</Label>
              <Input value={form.designation} onChange={e => set("designation", e.target.value)} data-testid="em-designation" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Department</Label>
              <Input value={form.department} onChange={e => set("department", e.target.value)} data-testid="em-department" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Employment Type</Label>
              <Input value={form.employment_type} onChange={e => set("employment_type", e.target.value)} data-testid="em-employment-type" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Date of Joining</Label>
              <DatePicker value={form.date_of_joining} onChange={v => set("date_of_joining", v)} testid="em-doj" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Years of Service</Label>
              <Input value={form.years_of_service} onChange={e => set("years_of_service", e.target.value)} data-testid="em-yos" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold">Educational Qualification</Label>
              <Input value={form.educational_qualification} onChange={e => set("educational_qualification", e.target.value)} data-testid="em-qualification" />
            </div>
            <div>
              <Label className="text-xs font-semibold">District *</Label>
              <Select value={form.district} onValueChange={v => setForm(f => ({ ...f, district: v, block_panchayat: "", local_body_name: "" }))}>
                <SelectTrigger data-testid="em-district"><SelectValue placeholder="Select district" /></SelectTrigger>
                <SelectContent>
                  {DISTRICTS.map(d => <SelectItem key={d.slug} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Local Body Type</Label>
              <Select value={form.local_body_type} onValueChange={v => setForm(f => ({ ...f, local_body_type: v, block_panchayat: "", local_body_name: "" }))}>
                <SelectTrigger data-testid="em-lb-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {LOCAL_BODY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {lbType === "Grama Panchayat" && (
              <div>
                <Label className="text-xs font-semibold">Block Panchayat</Label>
                <Select value={form.block_panchayat} onValueChange={v => setForm(f => ({ ...f, block_panchayat: v, local_body_name: "" }))}>
                  <SelectTrigger data-testid="em-block"><SelectValue placeholder="Select Block" /></SelectTrigger>
                  <SelectContent>
                    {(BLOCK_PANCHAYATS_BY_DISTRICT[district] || []).map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {lbType && lbType !== "Grama Panchayat" && (
              <div>
                <Label className="text-xs font-semibold">Local Body Name</Label>
                {lbType === "Block Panchayat"
                  ? lbNameSelect("Select Block Panchayat", BLOCK_PANCHAYATS_BY_DISTRICT[district], "em-lb-name")
                  : lbType === "Municipality"
                  ? lbNameSelect("Select Municipality", MUNICIPALITIES_BY_DISTRICT[district], "em-lb-name")
                  : lbType === "Corporation"
                  ? lbNameSelect("Select Corporation", CORPORATIONS_BY_DISTRICT[district], "em-lb-name")
                  : lbNameSelect("Select District Panchayat", DISTRICT_PANCHAYATS_BY_DISTRICT[district], "em-lb-name")}
              </div>
            )}
            {lbType === "Grama Panchayat" && (
              <div>
                <Label className="text-xs font-semibold">Grama Panchayat</Label>
                <Select value={form.local_body_name} onValueChange={v => set("local_body_name", v)} disabled={!form.block_panchayat}>
                  <SelectTrigger data-testid="em-lb-name"><SelectValue placeholder={form.block_panchayat ? "Select Grama Panchayat" : "Select Block Panchayat first"} /></SelectTrigger>
                  <SelectContent>
                    {((GRAMA_PANCHAYATS_BY_DISTRICT_BLOCK[district] || {})[form.block_panchayat] || []).map(gp => <SelectItem key={gp} value={gp}>{gp}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="em-submit">
              <Pencil className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
